/**
 * Window Management Commands - Opening, closing, and managing windows within a space
 *
 * Handles all window lifecycle operations: open/close individual entities,
 * create/update/remove/list/focus named windows, and find windows by entity.
 * Coordinates between entityStore, spaceStore, contextStore, and spatialUIStore.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import useEntityStore from '../../stores/entityStore';
import useSpaceStore from '../../stores/spaceStore';
import { useSpatialUIStore } from '../../stores/spaceSpatialUIStore';
import { useContextStore } from '../../components/Space/SpaceChat/store/contextStore';
import { getAppTypeForEntity } from '../../utils/entityTypeMapping';
import { useCommandRegistryStore } from '../../stores/commandRegistryStore';
import { chatCreateThreadCommand, chatSendMessageCommand } from '../../components/Space/SpaceChat/commands/chat_thread';

const log = createFileLogger('WINDOW_COMMANDS', { enabled: false });

// ====== WINDOW OPEN ======

const windowOpenCommand: SelfContainedCommand = {
  id: 'space_open_window',
  description: 'Open an entity (document, note, PDF, etc.) in a new window',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'The ID of the entity to open',
        required: true
      },
      {
        name: 'position',
        type: 'object',
        description: 'Optional window position {x, y}',
        required: false
      },
      {
        name: 'size',
        type: 'object',
        description: 'Optional window size {width, height}',
        required: false
      },
      {
        name: 'navigationTarget',
        type: 'object',
        description: 'Optional navigation target for PDF/document (e.g., {pageIndex: 0, startIndex: 10, endIndex: 50, highlightColor: "#10b981"})',
        required: false
      },
      {
        name: 'activate',
        type: 'boolean',
        description: 'Whether to bring window to front immediately. Set to false when creating from sidebar to preserve input focus. Default: true',
        required: false
      }
    ]
  },

  examples: {
    good: [
      {
        input: { entityId: 'doc_123' },
        expectedOutput: createSuccessResult('Window opened', { windowId: 'window_123' }),
        description: 'Open a document in a new window',
        context: 'When user wants to view a document'
      },
      {
        input: { entityId: 'note_456', activate: false },
        expectedOutput: createSuccessResult('Window opened', { windowId: 'window_456' }),
        description: 'Create window in background without stealing focus',
        context: 'When creating entity from sidebar with rename input'
      }
    ],
    bad: []
  },

  performance: { expectedDuration: 300, complexity: 'medium' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId, position, size, navigationTarget, activate = true } = params;
    const startTime = Date.now();

    console.log(`🚀 [space_open_window] START (t=0ms): ${entityId}`);

    try {
      const { windows } = useSpaceStore.getState();
      const entityStore = useEntityStore.getState();

      console.log(`[space_open_window] Checking cache (t=${Date.now() - startTime}ms)`);

      let entity = entityStore.entities.find((e: any) =>
        e._id === entityId || e.id === entityId || e.llmId === entityId
      );

      if (entity) {
        const targetEntityId = entity._id || entity.id;
        const targetLlmId = entity.llmId;

        console.log(`[space_open_window] Entity found in cache (t=${Date.now() - startTime}ms), checking for existing window`);

        const existingEntry = Object.entries(windows || {}).find(([_id, w]: any) => {
          const win = w || {};
          const llmIdMatch = win.entityLlmId && targetLlmId && win.entityLlmId === targetLlmId;
          const entityIdMatch = win.entityId && targetEntityId && win.entityId === targetEntityId;
          // Entities without llmId (e.g. Automation, Schedule, Job) match by entityId alone
          return targetLlmId ? (llmIdMatch && entityIdMatch) : entityIdMatch;
        });

        if (existingEntry) {
          const [existingWindowId, existingWindow] = existingEntry as [string, any];
          const expectedAppType = getAppTypeForEntity(entity);
          const currentAppType = existingWindow.appType;

          if (currentAppType !== expectedAppType) {
            console.log(`⚠️ [space_open_window] Window has wrong appType (t=${Date.now() - startTime}ms)`, {
              current: currentAppType,
              expected: expectedAppType,
              entityType: entity.entityType,
              willRecreate: true
            });
            useSpaceStore.getState().actions.closeWindow(existingWindowId);
          } else {
            console.log(`✅ [space_open_window] Window exists with correct appType, focusing immediately (t=${Date.now() - startTime}ms)`);

            if (navigationTarget) {
              console.log('[space_open_window] Updating existing window with new navigation target:', navigationTarget);
              useSpaceStore.getState().actions.updateWindow(existingWindowId, {
                applicationState: { navigationTarget }
              });
            }

            useSpaceStore.getState().bringWindowToFront(existingWindowId);
            useSpatialUIStore.getState().bringWindowToFront(existingWindowId);

            const duration = Date.now() - startTime;
            console.log(`✅ [space_open_window] DONE (t=${duration}ms) - focused existing window`);

            return createSuccessResult(
              `Focused existing window for ${entity.name}`,
              {
                windowId: existingWindowId,
                entity: {
                  id: targetEntityId,
                  name: entity.name,
                  type: entity.entityType
                }
              },
              { metadata: { executionTime: duration } }
            );
          }
        }

        // AppInstance entities (dock apps) may have had their entityId cleared from the
        // window by useWindowEntitySync. Fall back to matching by appType so clicking
        // the sidebar item focuses the existing window instead of creating a duplicate.
        if (entity.entityType === 'AppInstance') {
          const appTypeForCheck = getAppTypeForEntity(entity);
          const appTypeEntry = Object.entries(windows || {}).find(([_id, w]: any) => w?.appType === appTypeForCheck);
          if (appTypeEntry) {
            const [existingWindowId] = appTypeEntry;
            console.log(`✅ [space_open_window] AppInstance — found existing window by appType '${appTypeForCheck}', focusing (t=${Date.now() - startTime}ms)`);
            useSpaceStore.getState().bringWindowToFront(existingWindowId);
            useSpatialUIStore.getState().bringWindowToFront(existingWindowId);
            const duration = Date.now() - startTime;
            return createSuccessResult(
              `Focused existing window for ${entity.name}`,
              { windowId: existingWindowId, entity: { id: targetEntityId, name: entity.name, type: entity.entityType } },
              { metadata: { executionTime: duration } }
            );
          }
        }
      }

      console.log(`[space_open_window] Fetching entity (t=${Date.now() - startTime}ms)`);
      entity = await useEntityStore.getState().actions.getEntity(entityId);
      console.log(`[space_open_window] Entity fetched (t=${Date.now() - startTime}ms)`);

      if (!entity) {
        return createErrorResult(
          'Entity not found',
          `No entity found with ID: ${entityId}`,
          'validation'
        );
      }

      const targetEntityId = entity._id || entity.id;
      const targetLlmId = entity.llmId;

      const existingEntry = Object.entries(windows || {}).find(([_id, w]: any) => {
        const win = w || {};
        const llmIdMatch = win.entityLlmId && targetLlmId && win.entityLlmId === targetLlmId;
        const entityIdMatch = win.entityId && targetEntityId && win.entityId === targetEntityId;
        // Entities without llmId (e.g. Automation, Schedule, Job) match by entityId alone
        return targetLlmId ? (llmIdMatch && entityIdMatch) : entityIdMatch;
      });

      if (existingEntry) {
        const [existingWindowId, existingWindow] = existingEntry as [string, any];
        const expectedAppType = getAppTypeForEntity(entity);
        const currentAppType = existingWindow.appType;

        if (currentAppType !== expectedAppType) {
          console.log(`⚠️ [space_open_window] Window found after fetch but has wrong appType (t=${Date.now() - startTime}ms)`, {
            current: currentAppType,
            expected: expectedAppType,
            entityType: entity.entityType,
            willRecreate: true
          });
          useSpaceStore.getState().actions.closeWindow(existingWindowId);
        } else {
          console.log(`[space_open_window] Window found after fetch with correct appType (t=${Date.now() - startTime}ms), focusing`);

          if (navigationTarget) {
            console.log('[space_open_window] Updating existing window with new navigation target:', navigationTarget);
            useSpaceStore.getState().actions.updateWindow(existingWindowId, {
              applicationState: { navigationTarget }
            });
          }

          useSpaceStore.getState().bringWindowToFront(existingWindowId);
          useSpatialUIStore.getState().bringWindowToFront(existingWindowId);

          const duration = Date.now() - startTime;
          return createSuccessResult(
            `Focused existing window for ${entity.name}`,
            {
              windowId: existingWindowId,
              entity: {
                id: targetEntityId,
                name: entity.name,
                type: entity.entityType
              }
            },
            { metadata: { executionTime: duration } }
          );
        }
      }

      // AppInstance entities (dock apps) may have had their entityId cleared from the
      // window by useWindowEntitySync. Fall back to matching by appType.
      if (entity.entityType === 'AppInstance') {
        const appTypeForCheck = getAppTypeForEntity(entity);
        const appTypeEntry = Object.entries(windows || {}).find(([_id, w]: any) => w?.appType === appTypeForCheck);
        if (appTypeEntry) {
          const [existingWindowId] = appTypeEntry;
          console.log(`✅ [space_open_window] AppInstance — found existing window by appType '${appTypeForCheck}' (post-fetch), focusing (t=${Date.now() - startTime}ms)`);
          useSpaceStore.getState().bringWindowToFront(existingWindowId);
          useSpatialUIStore.getState().bringWindowToFront(existingWindowId);
          const duration = Date.now() - startTime;
          return createSuccessResult(
            `Focused existing window for ${entity.name}`,
            { windowId: existingWindowId, entity: { id: targetEntityId, name: entity.name, type: entity.entityType } },
            { metadata: { executionTime: duration } }
          );
        }
      }

      const appType = getAppTypeForEntity(entity);

      // Automation and OrchestratorAgent entities open in standalone mode (Apple Notes pattern):
      // clicking an individual item shows only its detail view, not the full list.
      const isAutomation = entity.entityType === 'Automation' || entity.entityType === 'Agent' || entity.entityType === 'OrchestratorAgent';
      if (isAutomation) {
        log.info('[space_open_window] Automation/Agent entity — using standalone renderMode');
      }

      console.log(`[space_open_window] Creating window (t=${Date.now() - startTime}ms)`);
      const windowId = await useSpaceStore.getState().actions.createWindow({
        appType,
        entity,
        position: position || { x: 100, y: 100 },
        size: size || { width: 800, height: 600 },
        ...(isAutomation ? { props: { renderMode: 'standalone' } } : {}),
        applicationState: navigationTarget ? { navigationTarget } : undefined
      });
      console.log(`[space_open_window] Window created (t=${Date.now() - startTime}ms)`);

      if (activate) {
        console.log(`[space_open_window] Activating window immediately`);
        useSpaceStore.getState().bringWindowToFront(windowId);
        useSpatialUIStore.getState().bringWindowToFront(windowId);

        if (entity.llmId) {
          const contextStore = useContextStore.getState();
          const { isContextEligibleEntity, getContextAppType } = await import('../../utils/entityContextEligibility');

          if (isContextEligibleEntity(entity)) {
            const contextAppType = getContextAppType(entity);

            contextStore.addEntityReference(entity.llmId, {
              title: entity.name,
              appType: contextAppType,
              entityType: entity.entityType,
              isActive: true,
              windowId,
              documentRole: 'currentDoc'
            });

            console.log(`[space_open_window] ✅ Added ${entity.entityType} to context as currentDoc:`, {
              entityName: entity.name,
              appType: contextAppType,
              llmId: entity.llmId
            });
          } else {
            console.log(`[space_open_window] ℹ️ Entity not eligible for context:`, {
              entityType: entity.entityType,
              entityName: entity.name
            });
          }
        }
      } else {
        console.log(`[space_open_window] Window created in background (activate: false) - context will be updated by subscription when activated`);
      }

      const duration = Date.now() - startTime;
      console.log(`✅ [space_open_window] DONE (t=${duration}ms) - created new window`);

      return createSuccessResult(
        `Opened ${entity.entityType}: ${entity.name}`,
        {
          windowId,
          appType,
          entity: {
            id: entity._id,
            name: entity.name,
            type: entity.entityType
          }
        },
        { metadata: { executionTime: duration } }
      );
    } catch (error: any) {
      const duration = Date.now() - startTime;
      console.error(`❌ [space_open_window] ERROR (t=${duration}ms):`, error);
      return createErrorResult(
        'Failed to open window',
        error.message || 'Unknown error',
        'execution',
        { metadata: { executionTime: duration } }
      );
    }
  }
};

// ====== WINDOW CLOSE ======

const windowCloseCommand: SelfContainedCommand = {
  id: 'space_close_window',
  description: 'Close a window and clean up its context',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'windowId',
        type: 'string',
        description: 'The ID of the window to close',
        required: true
      }
    ]
  },

  examples: {
    good: [
      {
        input: { windowId: 'window_123' },
        expectedOutput: createSuccessResult('Window closed', {}),
        description: 'Close a window',
        context: 'When user wants to close a window'
      }
    ],
    bad: []
  },

  performance: { expectedDuration: 100, complexity: 'low' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { windowId } = params;

    try {
      log.info('[space_close_window] Closing window:', { windowId });

      const windows = useSpaceStore.getState().windows;
      const window = windows[windowId];

      if (window?.appType === 'browser') {
        log.info('[space_close_window] Detected browser window, triggering view cleanup');

        const cleanupRegistry = (window as any).__browserAppCleanup;
        const cleanupFn = cleanupRegistry?.get(windowId);

        if (cleanupFn) {
          try {
            await cleanupFn();
            log.info('[space_close_window] Browser views cleaned up successfully');
          } catch (err) {
            log.error('[space_close_window] Error during browser view cleanup:', err);
          }
        } else {
          log.warn('[space_close_window] No cleanup function registered for browser window:', windowId);
        }
      }

      const entity = useSpaceStore.getState().getWindowEntity(windowId);

      if (entity?.llmId) {
        const contextStore = useContextStore.getState();
        const refs = contextStore.getEntityReferences();
        const ref = refs.find((r: any) => r.windowId === windowId);
        if (ref) {
          contextStore.removeEntityReference(entity.llmId);
        }
      }

      useSpaceStore.getState().actions.closeWindow(windowId);

      log.info('[space_close_window] Window closed successfully:', { windowId });

      return createSuccessResult(
        'Window closed',
        { windowId },
        { metadata: { executionTime: Date.now() - startTime } }
      );
    } catch (error: any) {
      log.error('[space_close_window] Error closing window:', error);
      return createErrorResult(
        'Failed to close window',
        error.message || 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

// ====== SPACE CREATE WINDOW ======

const spaceCreateWindowCommand: SelfContainedCommand = {
  id: 'space_createWindow',
  description: 'Create a new window in the space with the specified app type. Returns the created window ID and details.',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'appType',
        type: 'string',
        description: 'The type of app to open in the window',
        required: true,
        pattern: /^(docs|notes|browser|pdfium|chat|console|markdown|image|finder|store|workflow|settings)$/,
        examples: ['docs', 'notes', 'browser', 'pdfium']
      },
      {
        name: 'title',
        type: 'string',
        description: 'Optional title for the window (defaults to app name)',
        required: false,
        validator: (value: string) => {
          if (value && value.length > 100) return 'Title too long (max 100 chars)';
          return null;
        },
        examples: ['My Document', 'Research Notes', 'Meeting Minutes']
      },
      {
        name: 'position',
        type: 'object',
        description: 'Optional position for the window {x: number, y: number}',
        required: false,
        defaultValue: { x: 100, y: 100 },
        validator: (value: any) => {
          if (value && (typeof value.x !== 'number' || typeof value.y !== 'number')) {
            return 'Position must have numeric x and y properties';
          }
          return null;
        },
        examples: [{ x: 100, y: 100 }, { x: 200, y: 150 }]
      },
      {
        name: 'size',
        type: 'object',
        description: 'Optional size for the window {width: number, height: number}',
        required: false,
        defaultValue: { width: 800, height: 600 },
        validator: (value: any) => {
          if (value && (typeof value.width !== 'number' || typeof value.height !== 'number')) {
            return 'Size must have numeric width and height properties';
          }
          if (value && (value.width < 100 || value.height < 100)) {
            return 'Window too small (min 100x100)';
          }
          return null;
        },
        examples: [{ width: 800, height: 600 }, { width: 1200, height: 800 }]
      },
      {
        name: 'entity',
        type: 'object',
        description: 'Optional entity to associate with the window',
        required: false,
        examples: [{ id: 'doc-123', name: 'My Document' }]
      },
      {
        name: 'props',
        type: 'object',
        description: 'Optional props to pass to the window component',
        required: false,
        examples: [{ theme: 'dark', fontSize: 'large' }]
      }
    ]
  },

  examples: {
    good: [
      {
        input: { appType: 'docs', title: 'My Document' },
        expectedOutput: {
          status: 'success',
          message: '✅ Window created successfully',
          format: 'json',
          data: {
            window: {
              id: 'window-123',
              appType: 'docs',
              title: 'My Document',
              position: { x: 100, y: 100 },
              size: { width: 800, height: 600 }
            }
          }
        } as StandardToolResult,
        description: 'Create a docs window with custom title',
        context: 'When needing a new document workspace'
      }
    ],
    bad: [
      {
        input: { appType: 'invalid-app' },
        error: 'Invalid app type',
        why: 'App type must be one of the supported types',
        fix: 'Use a valid app type like "docs", "notes", "browser", etc.'
      },
      {
        input: { appType: 'docs', size: { width: 50, height: 50 } },
        error: 'Window too small (min 100x100)',
        why: 'Window dimensions are below minimum requirements',
        fix: 'Use dimensions of at least 100x100 pixels'
      }
    ]
  },

  performance: { expectedDuration: 200, complexity: 'low' },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const {
      appType,
      title,
      position = { x: 100, y: 100 },
      size = { width: 800, height: 600 },
      entity,
      props
    } = params;

    try {
      const windowTitle = title || (appType.charAt(0).toUpperCase() + appType.slice(1));
      let finalEntity = entity;

      if (appType === 'browser' && !entity) {
        console.log('[space_createWindow] Creating BrowserWindow entity for browser app');
        const { entityService } = await import('../../services/entityService');
        const browserWindowEntity = await entityService.create({
          entityType: 'BrowserWindow',
          name: 'Browser',
          skeleton: {
            '@type': 'BrowserWindow',
            tabs: [],
            activeTabId: null,
            settings: {
              homeUrl: 'https://www.google.com',
              searchEngine: 'google'
            }
          }
        } as any);

        finalEntity = browserWindowEntity;
        console.log('[space_createWindow] ✅ BrowserWindow entity created:', browserWindowEntity._id || browserWindowEntity.id);
      }

      const { actions } = useSpaceStore.getState();
      const windowId = await actions.createWindow({
        appType,
        title: windowTitle,
        entity: finalEntity,
        position,
        size,
        applicationState: props ? { props } : undefined
      });

      log.info('[space_createWindow] Created window using actions.createWindow:', {
        windowId,
        appType,
        hasEntity: !!finalEntity
      });

      actions.setActiveWindow(windowId);
      useSpaceStore.getState().bringWindowToFront(windowId);

      const { useSpatialUIStore: spatialStore } = await import('../../stores/spaceSpatialUIStore');
      spatialStore.getState().bringWindowToFront(windowId);

      if (finalEntity?.llmId) {
        try {
          const { useContextStore: ctxStore } = await import('../../components/Space/SpaceChat/store/contextStore');
          const contextStore = ctxStore.getState();
          const { isContextEligibleEntity, getContextAppType } = await import('../../utils/entityContextEligibility');

          if (isContextEligibleEntity(finalEntity)) {
            const contextAppType = getContextAppType(finalEntity);

            contextStore.addEntityReference(finalEntity.llmId, {
              title: finalEntity.name || windowTitle,
              appType: contextAppType,
              entityType: finalEntity.entityType,
              isActive: true,
              windowId,
              documentRole: 'currentDoc'
            });

            log.info('[space_createWindow] ✅ Entity added to context store:', {
              entityType: finalEntity.entityType,
              entityName: finalEntity.name,
              appType: contextAppType,
              llmId: finalEntity.llmId
            });
          } else {
            log.info('[space_createWindow] ℹ️ Entity not eligible for context:', {
              entityType: finalEntity.entityType,
              entityName: finalEntity.name
            });
          }
        } catch (contextError) {
          log.warn('[space_createWindow] Failed to add window to context store:', contextError);
        }
      }

      return createSuccessResult(
        `✅ Window created successfully: ${windowTitle}`,
        {
          window: {
            id: windowId,
            appType,
            title: windowTitle,
            position,
            size,
            ...(entity && { entity }),
            ...(props && { props })
          }
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'self-contained-v2',
            affectedEntities: [windowId]
          },
          ui: {
            autoFocus: windowId,
            refreshContext: true,
            resultPreview: 'summary'
          }
        }
      );
    } catch (error) {
      log.error('[space_createWindow] Error:', error);
      return createErrorResult(
        'Failed to create window',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        {
          data: {
            spaceId: context.spaceId,
            requestedAppType: appType
          },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

// ====== SPACE UPDATE WINDOW ======

const spaceUpdateWindowCommand: SelfContainedCommand = {
  id: 'space_updateWindow',
  description: 'Update properties of an existing window (position, size, title, etc.)',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',

  schema: {
    parameters: [
      {
        name: 'windowId',
        type: 'string',
        description: 'The ID of the window to update',
        required: true,
        pattern: /^window-[a-zA-Z0-9-_]+$/,
        examples: ['window-1753937565712-1nrwc4', 'window-123-abc']
      },
      {
        name: 'updates',
        type: 'object',
        description: 'Object containing the properties to update (position, size, title, etc.)',
        required: true,
        validator: (value: any) => {
          if (!value || typeof value !== 'object') return 'Updates must be an object';
          if (Object.keys(value).length === 0) return 'Updates object cannot be empty';
          return null;
        },
        examples: [
          { title: 'Updated Title' },
          { position: { x: 200, y: 150 } },
          { size: { width: 1000, height: 700 } }
        ]
      }
    ]
  },

  examples: {
    good: [
      {
        input: {
          windowId: 'window-1753937565712-1nrwc4',
          updates: { title: 'Updated Document' }
        },
        expectedOutput: {
          status: 'success',
          message: '✅ Window updated successfully',
          format: 'json'
        } as StandardToolResult,
        description: 'Update window title',
        context: 'When needing to rename a window'
      }
    ],
    bad: [
      {
        input: { windowId: 'window-123', updates: {} },
        error: 'Updates object cannot be empty',
        why: 'No properties specified to update',
        fix: 'Include at least one property to update'
      }
    ]
  },

  performance: { expectedDuration: 100, complexity: 'low' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { windowId, updates } = params;

    try {
      const { updateWindow, windows, saveToBackend } = useSpaceStore.getState();

      if (!windows[windowId]) {
        return createErrorResult(
          'Window not found',
          `Window ${windowId} does not exist in the current space`,
          'validation',
          {
            data: {
              requestedWindowId: windowId,
              availableWindows: Object.keys(windows)
            },
            metadata: { executionTime: Date.now() - startTime }
          }
        );
      }

      log.info('[space_updateWindow] Updating window:', { windowId, updates });

      updateWindow(windowId, updates);
      const updatedWindow = useSpaceStore.getState().windows[windowId];

      try {
        await saveToBackend();
      } catch (backendError) {
        log.warn('[space_updateWindow] Backend save failed, but window updated locally:', backendError);
      }

      return createSuccessResult(
        `✅ Window updated successfully: ${updatedWindow.title || updatedWindow.appType}`,
        {
          window: {
            id: updatedWindow.id,
            title: updatedWindow.title,
            appType: updatedWindow.appType,
            position: updatedWindow.position,
            size: updatedWindow.size,
            entity: (() => {
              const entity = useSpaceStore.getState().getWindowEntity(updatedWindow.id);
              return entity ? {
                id: entity._id || entity.id,
                name: entity.name
              } : undefined;
            })()
          }
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'self-contained-v2',
            affectedEntities: [windowId]
          },
          ui: { refreshContext: true, resultPreview: 'minimal' }
        }
      );
    } catch (error) {
      log.error('[space_updateWindow] Error:', error);
      return createErrorResult(
        'Failed to update window',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        {
          data: { windowId, updates },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

// ====== SPACE REMOVE WINDOW ======

const spaceRemoveWindowCommand: SelfContainedCommand = {
  id: 'space_removeWindow',
  description: 'Remove/close a window from the space',
  autonomyLevel: 'confirm',
  visualTreatment: 'narrative',

  schema: {
    parameters: [
      {
        name: 'windowId',
        type: 'string',
        description: 'The ID of the window to remove',
        required: true,
        pattern: /^window-[a-zA-Z0-9-_]+$/,
        examples: ['window-1753937565712-1nrwc4', 'window-123-abc']
      }
    ]
  },

  examples: {
    good: [
      {
        input: { windowId: 'window-1753937565712-1nrwc4' },
        expectedOutput: {
          status: 'success',
          message: '✅ Window removed successfully',
          format: 'json'
        } as StandardToolResult,
        description: 'Remove a window from the space',
        context: 'When closing a window that is no longer needed'
      }
    ],
    bad: [
      {
        input: { windowId: 'invalid-id' },
        error: 'Window not found',
        why: 'Window ID does not exist in current space',
        fix: 'Use a valid window ID from the current space'
      }
    ]
  },

  performance: { expectedDuration: 100, complexity: 'low' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { windowId } = params;

    try {
      const { removeWindow, windows, saveToBackend } = useSpaceStore.getState();

      if (!windows[windowId]) {
        return createErrorResult(
          'Window not found',
          `Window ${windowId} does not exist in the current space`,
          'validation',
          {
            data: {
              requestedWindowId: windowId,
              availableWindows: Object.keys(windows)
            },
            metadata: { executionTime: Date.now() - startTime }
          }
        );
      }

      const windowToRemove = windows[windowId];
      log.info('[space_removeWindow] Removing window:', windowId, windowToRemove.title);

      removeWindow(windowId);

      try {
        await saveToBackend();
      } catch (backendError) {
        log.warn('[space_removeWindow] Backend save failed, but window removed locally:', backendError);
      }

      return createSuccessResult(
        `✅ Window removed successfully: ${windowToRemove.title || windowToRemove.appType}`,
        {
          windowId,
          removedWindow: {
            title: windowToRemove.title,
            appType: windowToRemove.appType,
            entity: (() => {
              const entity = useSpaceStore.getState().getWindowEntity(windowId);
              return entity ? { id: entity._id || entity.id, name: entity.name } : undefined;
            })()
          }
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'self-contained-v2',
            affectedEntities: [windowId]
          },
          ui: { refreshContext: true, resultPreview: 'summary' }
        }
      );
    } catch (error) {
      log.error('[space_removeWindow] Error:', error);
      return createErrorResult(
        'Failed to remove window',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        {
          data: { windowId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

// ====== SPACE LIST WINDOWS ======

const spaceListWindowsCommand: SelfContainedCommand = {
  id: 'space_list_windows',
  description: 'Get a list of all windows in the space with their details',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',

  schema: {
    parameters: [
      {
        name: 'includeContent',
        type: 'boolean',
        description: 'Whether to include content metadata in the listing',
        required: false,
        defaultValue: false,
        examples: [true, false]
      },
      {
        name: 'filterByAppType',
        type: 'string',
        description: 'Optional filter to only show windows of a specific app type',
        required: false,
        pattern: /^(docs|notes|browser|pdfium|chat|console|markdown|image|finder|store|workflow|settings)$/,
        examples: ['docs', 'notes', 'browser']
      }
    ]
  },

  examples: {
    good: [
      {
        input: {},
        expectedOutput: {
          status: 'success',
          message: '✅ Windows listed successfully',
          format: 'json',
          data: { windows: [{ id: 'window-123', appType: 'docs', title: 'My Document' }], count: 1 }
        } as StandardToolResult,
        description: 'List all windows in the space',
        context: 'When needing to see all open windows'
      }
    ],
    bad: []
  },

  performance: { expectedDuration: 50, complexity: 'low' },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { includeContent = false, filterByAppType } = params;

    try {
      const { windows } = useSpaceStore.getState();
      const windowContents = (context.spaceContext as any)?.entityRefs || {};

      let windowList = Object.values(windows).map(window => {
        const baseWindow = {
          id: window.id,
          type: window.type,
          appType: window.appType,
          title: window.title,
          position: window.position,
          size: window.size,
          entity: (() => {
            const entity = useSpaceStore.getState().getWindowEntity(window.id);
            return entity ? {
              id: entity.id || entity._id,
              name: entity.name,
              entityType: entity.entityType
            } : undefined;
          })()
        };

        if (includeContent) {
          const content = (windowContents as any)[window.id];
          const contentText = typeof content === 'string' ? content : content?.content || '';
          return {
            ...baseWindow,
            contentMetadata: {
              hasContent: !!contentText,
              contentLength: contentText.length,
              lastModified: Date.now()
            }
          };
        }

        return baseWindow;
      });

      if (filterByAppType) {
        const originalCount = windowList.length;
        windowList = windowList.filter(window => window.appType === filterByAppType);
        log.log(`[space_list_windows] Filtered by appType "${filterByAppType}": ${originalCount} → ${windowList.length} windows`);
      }

      return createSuccessResult(
        `✅ Windows listed successfully: ${windowList.length} window${windowList.length !== 1 ? 's' : ''}${filterByAppType ? ` (${filterByAppType} only)` : ''}`,
        {
          windows: windowList,
          count: windowList.length,
          spaceId: context.spaceId,
          filter: filterByAppType ? { appType: filterByAppType } : undefined,
          includesContent: includeContent
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'self-contained-v2',
            affectedEntities: windowList.map(w => w.id)
          },
          ui: { refreshContext: false, resultPreview: 'summary' }
        }
      );
    } catch (error) {
      log.error('[space_list_windows] Error:', error);
      return createErrorResult(
        'Failed to list windows',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        {
          data: { spaceId: context.spaceId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

// ====== SPACE FOCUS WINDOW ======

const spaceFocusWindowCommand: SelfContainedCommand = {
  id: 'space_focus_window',
  description: 'Bring a window to the front and focus it',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',

  schema: {
    parameters: [
      {
        name: 'windowId',
        type: 'string',
        description: 'The ID of the window to focus',
        required: true,
        pattern: /^window-[a-zA-Z0-9-_]+$/,
        examples: ['window-1753937565712-1nrwc4', 'window-123-abc']
      }
    ]
  },

  examples: {
    good: [
      {
        input: { windowId: 'window-1753937565712-1nrwc4' },
        expectedOutput: {
          status: 'success',
          message: '✅ Window focused successfully',
          format: 'json'
        } as StandardToolResult,
        description: 'Focus a window',
        context: 'When needing to bring a specific window to the front'
      }
    ],
    bad: [
      {
        input: { windowId: 'invalid-id' },
        error: 'Window not found',
        why: 'Window ID does not exist in current space',
        fix: 'Use a valid window ID from the current space'
      }
    ]
  },

  performance: { expectedDuration: 50, complexity: 'low' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { windowId } = params;

    try {
      const { bringWindowToFront, windows, updateWindow } = useSpaceStore.getState();

      if (!windows[windowId]) {
        return createErrorResult(
          'Window not found',
          `Window ${windowId} does not exist in the current space`,
          'validation',
          {
            data: {
              requestedWindowId: windowId,
              availableWindows: Object.keys(windows)
            },
            metadata: { executionTime: Date.now() - startTime }
          }
        );
      }

      const window = windows[windowId];
      log.info('[space_focus_window] Focusing window:', windowId, window.appType);

      const windowEntity = useSpaceStore.getState().getWindowEntity(window.id);

      if (windowEntity && !window.entityId) {
        const entityId = windowEntity._id || windowEntity.id;
        log.debug('[space_focus_window] Migrating window to root-level entityId:', { windowId, entityId, appType: window.appType });

        updateWindow(windowId, {
          entityId,
          entityLlmId: windowEntity.llmId
        });
      }

      bringWindowToFront(windowId);

      return createSuccessResult(
        `✅ Window focused successfully: ${window.title || window.appType}`,
        {
          windowId,
          appType: window.appType,
          title: window.title,
          hasEntity: !!windowEntity,
          entityId: windowEntity?._id || windowEntity?.id,
          position: window.position,
          size: window.size
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'self-contained',
            affectedEntities: [windowId]
          },
          ui: { autoFocus: windowId, refreshContext: false, resultPreview: 'minimal' }
        }
      );
    } catch (error) {
      log.error('[space_focus_window] Error:', error);
      return createErrorResult(
        'Failed to focus window',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        {
          data: { windowId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

// ====== SPACE OPEN ENTITY ======

const spaceOpenEntityCommand: SelfContainedCommand = {
  id: 'space_openEntity',
  description: 'Open an entity in the appropriate app/window based on its type and properties',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'entity',
        type: 'object',
        description: 'The entity to open',
        required: true,
        validator: (value: any) => {
          if (!value || typeof value !== 'object') return 'Entity must be an object';
          if (!value._id && !value.id && !value.llmId) return 'Entity must have an id, _id, or llmId field';
          return null;
        },
        examples: [
          { _id: 'doc-123', name: 'My Document', entityType: 'Doc' },
          { id: 'note-456', title: 'My Notes', type: 'Note' }
        ]
      },
      {
        name: 'position',
        type: 'object',
        description: 'Optional position for the window {x: number, y: number}',
        required: false,
        defaultValue: { x: 100, y: 100 },
        validator: (value: any) => {
          if (value && (typeof value.x !== 'number' || typeof value.y !== 'number')) {
            return 'Position must have numeric x and y properties';
          }
          return null;
        },
        examples: [{ x: 100, y: 100 }, { x: 200, y: 150 }]
      },
      {
        name: 'size',
        type: 'object',
        description: 'Optional size for the window {width: number, height: number}',
        required: false,
        defaultValue: { width: 800, height: 600 },
        validator: (value: any) => {
          if (value && (typeof value.width !== 'number' || typeof value.height !== 'number')) {
            return 'Size must have numeric width and height properties';
          }
          if (value && (value.width < 100 || value.height < 100)) {
            return 'Window too small (min 100x100)';
          }
          return null;
        },
        examples: [{ width: 800, height: 600 }, { width: 1200, height: 800 }]
      }
    ]
  },

  examples: {
    good: [
      {
        input: {
          entity: { _id: 'doc-123', name: 'My Document', entityType: 'Doc' }
        },
        expectedOutput: {
          status: 'success',
          message: '✅ Entity opened successfully',
          format: 'json',
          data: {
            window: {
              id: 'window-123',
              appType: 'docs',
              title: 'My Document',
              entity: { id: 'doc-123', name: 'My Document' }
            }
          }
        } as StandardToolResult,
        description: 'Open a document entity in a docs window',
        context: 'When user wants to view or edit a document'
      }
    ],
    bad: [
      {
        input: { entity: null },
        error: 'Entity must be an object',
        why: 'Entity parameter is required and must be an object',
        fix: 'Provide a valid entity object with id and name/title'
      }
    ]
  },

  performance: { expectedDuration: 300, complexity: 'medium' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { entity, position = { x: 100, y: 100 }, size = { width: 800, height: 600 } } = params;

    try {
      log.info('[space_openEntity] Received entity:', {
        id: entity._id || entity.id || entity.llmId,
        entityType: entity.entityType,
        type: entity.type,
        name: entity.name,
        fullEntity: entity
      });

      let freshEntity = entity;
      const isComplete = entity.llmId && entity.entityType && entity.name;

      if (!isComplete) {
        log.info('[space_openEntity] Entity incomplete, fetching fresh data:', {
          entityId: entity._id || entity.id || entity.llmId,
          hasLlmId: !!entity.llmId,
          hasEntityType: !!entity.entityType,
          hasName: !!entity.name
        });

        try {
          const { entityService } = await import('../../services/entityService');
          freshEntity = await entityService.get(entity._id || entity.id || entity.llmId);

          log.info('[space_openEntity] ✅ Fresh entity fetched:', {
            entityId: freshEntity._id || freshEntity.id,
            name: freshEntity.name,
            hasLlmId: !!freshEntity.llmId,
            llmId: freshEntity.llmId,
            entityType: freshEntity.entityType
          });
        } catch (error) {
          log.warn('[space_openEntity] Failed to fetch fresh entity, using provided:', error);

          if (error instanceof Error && (error.message.includes('404') || error.message.includes('500'))) {
            log.error('[space_openEntity] ❌ Entity does not exist in backend:', {
              entityId: entity._id || entity.id || entity.llmId,
              entityName: entity.name,
              error: error.message
            });

            return createErrorResult(
              'Entity not found',
              `Entity "${entity.name || 'Unknown'}" no longer exists and cannot be opened.`,
              'validation',
              {
                data: {
                  entityId: entity._id || entity.id || entity.llmId,
                  entityName: entity.name
                }
              }
            );
          }

          freshEntity = entity;
        }
      } else {
        log.info('[space_openEntity] ✅ Using provided entity (already complete, no fetch needed):', {
          entityId: entity._id || entity.id,
          name: entity.name,
          llmId: entity.llmId,
          entityType: entity.entityType
        });
      }

      // Guard: if window for this entity already exists, focus it
      try {
        const { windows, bringWindowToFront } = useSpaceStore.getState();
        const targetEntityId = freshEntity._id || freshEntity.id;
        const targetLlmId = freshEntity.llmId;

        log.info('[space_openEntity] 🔍 Looking for existing window:', {
          targetEntityId,
          targetLlmId,
          targetEntityName: freshEntity.name
        });

        const existingEntry = Object.entries(windows || {}).find(([_id, w]: any) => {
          const win = w || {};
          const llmIdMatch = win.entityLlmId && targetLlmId && win.entityLlmId === targetLlmId;
          const entityIdMatch = win.entityId && targetEntityId && win.entityId === targetEntityId;
          return llmIdMatch && entityIdMatch;
        });

        if (existingEntry) {
          const [existingWindowId, existingWindow] = existingEntry as [string, any];
          log.info('[space_openEntity] ✅ Entity already open, focusing existing window', {
            existingWindowId,
            appType: existingWindow?.appType,
            title: existingWindow?.title
          });
          bringWindowToFront(existingWindowId);

          return createSuccessResult(
            `✅ Entity already open: ${freshEntity.name || freshEntity.title || 'Untitled'}`,
            {
              window: {
                id: existingWindowId,
                appType: existingWindow?.appType,
                title: existingWindow?.title,
                position: existingWindow?.position,
                size: existingWindow?.size,
                entity: {
                  id: targetEntityId,
                  name: freshEntity.name,
                  entityType: freshEntity.entityType || freshEntity.type
                }
              }
            },
            {
              format: 'json',
              metadata: {
                executionTime: Date.now() - startTime,
                method: 'self-contained',
                affectedEntities: [existingWindowId, targetEntityId]
              },
              ui: { autoFocus: existingWindowId, refreshContext: true, resultPreview: 'summary' }
            }
          );
        }
      } catch (guardErr) {
        log.warn('[space_openEntity] Existing-window guard failed (continuing to open new window):', guardErr);
      }

      const { executeCommand } = useCommandRegistryStore.getState();
      const result = await executeCommand('space_open_window', {
        entityId: freshEntity._id || freshEntity.id,
        position,
        size
      }) as StandardToolResult;

      if (result.status !== 'success') {
        return result;
      }

      const windowId = result.data.windowId;
      const entityType = entity.entityType || entity.type;

      const { windows } = useSpaceStore.getState();
      const window = windows[windowId];

      const isDocument = entityType === 'Doc' ||
                        entityType === 'Document' ||
                        window?.appType === 'docs' ||
                        window?.appType === 'docsApp';

      if (isDocument) {
        log.info('[space_openEntity] Auto-opening AI panel for document', {
          entityType,
          windowAppType: window?.appType,
          entityName: freshEntity?.name
        });
        const { setIsChatVisible } = useSpaceStore.getState();
        await setIsChatVisible(true);
      }

      return createSuccessResult(
        `✅ Entity opened successfully: ${entity.name || entity.title || 'Untitled'}`,
        {
          windowId,
          entity: {
            id: entity._id || entity.id || entity.llmId,
            name: entity.name,
            entityType: entityType
          }
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'self-contained',
            affectedEntities: [window?.id, entity._id || entity.id]
          },
          ui: { autoFocus: window?.id, refreshContext: true, resultPreview: 'summary' }
        }
      );
    } catch (error) {
      log.error('[space_openEntity] Error:', error);
      return createErrorResult(
        'Failed to open entity',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        {
          data: {
            entityId: entity?._id || entity?.id,
            entityType: entity?.entityType || entity?.type
          },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

// ====== MAIL OPEN SEARCH ======
// Receives a mail_open_search broadcastCmd from the AI agent (tool-runtime)
// and pushes the search results into the active MailApp window store.

const mailOpenSearchCommand: SelfContainedCommand = {
  id: 'mail_open_search',
  description: 'Apply AI-generated mail search results to the open MailApp window',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',

  schema: { parameters: [] },

  handler: async (params: {
    query?: string;
    semantic?: boolean;
    threadEntityIds?: string[];
  }) => {
    log.info('[mail_open_search] received', {
      query: params.query,
      threadCount: params.threadEntityIds?.length ?? 0,
      semantic: params.semantic,
    });

    const spaceStore = useSpaceStore.getState();
    const windows = spaceStore.windows ?? {};

    // Find the first open MailApp window ('mail' is the canonical appType; 'mail-app' is the alias)
    const mailWindow = Object.values(windows).find((w: any) => w.appType === 'mail' || w.appType === 'mail-app');

    let windowId: string;
    let spaceId: string;

    if (!mailWindow) {
      log.info('[mail_open_search] no MailApp window found — opening one');
      try {
        windowId = await spaceStore.actions.createWindow({
          appType: 'mail-app',
          title: 'Mail',
          position: { x: 100, y: 100 },
          size: { width: 1100, height: 750 },
        });
        spaceId = 'default';
        spaceStore.bringWindowToFront(windowId);
        useSpatialUIStore.getState().bringWindowToFront(windowId);
      } catch (err) {
        log.warn('[mail_open_search] failed to open MailApp window', { err });
        return createSuccessResult('failed to open MailApp window');
      }
    } else {
      windowId = (mailWindow as any).id;
      spaceId = (mailWindow as any).spaceId ?? 'default';

      // Bring the MailApp window to focus
      try {
        spaceStore.actions.setActiveWindow(windowId);
      } catch {
        // Non-fatal — window might already be active
      }
    }

    // Push search results into the MailApp store
    try {
      const { getMailAppStore } = await import('../../apps/MailApp/store/mailAppStore');
      const mailStore = getMailAppStore(windowId, spaceId).getState();

      if (params.threadEntityIds?.length) {
        mailStore.setBackendSearchResults(params.threadEntityIds, params.query ?? '');
        log.info('[mail_open_search] applied', {
          windowId,
          threadCount: params.threadEntityIds.length,
          query: params.query,
        });
      } else if (params.query) {
        mailStore.setSearchQuery(params.query);
        log.info('[mail_open_search] applied query only', { windowId, query: params.query });
      }
    } catch (err) {
      log.warn('[mail_open_search] failed to apply to mail store', { err });
    }

    return createSuccessResult('mail search applied');
  },
};

// ====== APP OPEN ======

const APP_TYPE_MAP: Record<string, string> = {
  drive: 'entityBrowser2',
  browser: 'browser',
  contacts: 'contacts',
  automations: 'automations',
  writing_dashboard: 'writers_dashboard',
};

const APP_TITLE_MAP: Record<string, string> = {
  drive: 'Drive',
  browser: 'Browser',
  contacts: 'Contacts',
  automations: 'Automations',
  writing_dashboard: 'Writing Hub',
};

const appOpenCommand: SelfContainedCommand = {
  id: 'app_open',
  description: 'Open an application window, focusing an existing one if already open',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'app',
        type: 'string',
        description: 'The application to open',
        required: true,
      },
    ],
  },

  examples: { good: [], bad: [] },
  performance: { expectedDuration: 300, complexity: 'low' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { app } = params;

    try {
      // Writing dashboard uses a special creation command
      if (app === 'writing_dashboard') {
        const { windows, bringWindowToFront } = useSpaceStore.getState();
        const existing = Object.entries(windows).find(([, w]: any) => w.appType === 'writers_dashboard');
        if (existing) {
          const [existingWindowId] = existing;
          bringWindowToFront(existingWindowId);
          useSpatialUIStore.getState().bringWindowToFront(existingWindowId);
          return createSuccessResult('Focused existing Writing Hub window', { windowId: existingWindowId });
        }
        await useCommandRegistryStore.getState().executeCommand('writers_dashboard_create', { title: 'Writing Hub' });
        return createSuccessResult('Opened Writing Hub', {}, { metadata: { executionTime: Date.now() - startTime } });
      }

      const appType = APP_TYPE_MAP[app];
      if (!appType) {
        return createErrorResult('Unknown app', `"${app}" is not a recognized application`, 'validation');
      }

      const { windows, actions, bringWindowToFront } = useSpaceStore.getState();

      // Focus existing window if one is open
      const existing = Object.entries(windows).find(([, w]: any) => w.appType === appType);
      if (existing) {
        const [existingWindowId] = existing;
        bringWindowToFront(existingWindowId);
        useSpatialUIStore.getState().bringWindowToFront(existingWindowId);
        return createSuccessResult(
          `Focused existing ${APP_TITLE_MAP[app]} window`,
          { windowId: existingWindowId },
          { metadata: { executionTime: Date.now() - startTime } }
        );
      }

      // Create a new window
      const windowId = await actions.createWindow({
        appType,
        title: APP_TITLE_MAP[app],
        position: { x: 100, y: 100 },
        size: { width: 1200, height: 800 },
      });
      actions.setActiveWindow(windowId);
      bringWindowToFront(windowId);
      useSpatialUIStore.getState().bringWindowToFront(windowId);

      return createSuccessResult(
        `Opened ${APP_TITLE_MAP[app]}`,
        { windowId, appType },
        { metadata: { executionTime: Date.now() - startTime } }
      );
    } catch (error: any) {
      log.error('[app_open] Error:', error);
      return createErrorResult('Failed to open app', error.message || 'Unknown error', 'execution', {
        metadata: { executionTime: Date.now() - startTime },
      });
    }
  },
};

// ====== BROWSER OPEN TAB ======

const browserOpenTabCommand: SelfContainedCommand = {
  id: 'browser_open_tab',
  description: 'Open a URL in a new browser tab, creating a browser window first if needed',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'url',
        type: 'string',
        description: 'The full URL to open',
        required: true,
      },
    ],
  },

  examples: { good: [], bad: [] },
  performance: { expectedDuration: 500, complexity: 'medium' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { url } = params;

    try {
      const { windows, actions, bringWindowToFront } = useSpaceStore.getState();

      // Find an existing browser window
      const existingBrowser = Object.entries(windows).find(([, w]: any) => w.appType === 'browser');

      if (!existingBrowser) {
        // No browser window open — delegate to browserwindow_create which creates the
        // BrowserWindow entity, creates the first BrowserPage entity, and opens the window.
        // Passing initialUrl sets the URL as the first tab, so no separate addTab needed.
        const result = await useCommandRegistryStore.getState().executeCommand('browserwindow_create', {
          initialUrl: url,
          persist: true,
        }) as StandardToolResult;

        const windowId = result?.data?.windowId;
        if (windowId) {
          bringWindowToFront(windowId);
          useSpatialUIStore.getState().bringWindowToFront(windowId);
        }

        return createSuccessResult(
          `Opened browser with tab: ${url}`,
          { windowId, url },
          { metadata: { executionTime: Date.now() - startTime } }
        );
      }

      // Browser window already exists — add a new tab to it
      const browserWindowId = existingBrowser[0];

      const windowEntity = useSpaceStore.getState().getWindowEntity(browserWindowId);
      const entityId = windowEntity?._id || windowEntity?.id || null;

      const { getBrowserAppStore } = await import('../../apps/BrowserApp/store/browserAppStore');

      if (entityId && !getBrowserAppStore(browserWindowId).getState().initialized) {
        await getBrowserAppStore(browserWindowId).getState().initialize(entityId);
      }

      await getBrowserAppStore(browserWindowId).getState().addTab(url);

      // Bring the browser window to front
      actions.setActiveWindow(browserWindowId);
      bringWindowToFront(browserWindowId);
      useSpatialUIStore.getState().bringWindowToFront(browserWindowId);

      return createSuccessResult(
        `Opened tab: ${url}`,
        { windowId: browserWindowId, url },
        { metadata: { executionTime: Date.now() - startTime } }
      );
    } catch (error: any) {
      log.error('[browser_open_tab] Error:', error);
      return createErrorResult('Failed to open browser tab', error.message || 'Unknown error', 'execution', {
        metadata: { executionTime: Date.now() - startTime },
      });
    }
  },
};

export const windowManagementCommands: SelfContainedCommand[] = [
  windowOpenCommand,
  windowCloseCommand,
  spaceCreateWindowCommand,
  spaceUpdateWindowCommand,
  spaceRemoveWindowCommand,
  spaceListWindowsCommand,
  spaceFocusWindowCommand,
  spaceOpenEntityCommand,
  chatCreateThreadCommand,
  chatSendMessageCommand,
  mailOpenSearchCommand,
  appOpenCommand,
  browserOpenTabCommand,
];
